// /opt/ptg01-v1/front-simulation/components/ErrorBoundary.tsx
// Error Boundary — catch les erreurs runtime côté client.
// Aligné sur front-admin pour homogénéité.
'use client';

import React, { Component, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('[ErrorBoundary]', error);
        console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    handleReload = () => {
        if (typeof window !== 'undefined') window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) return this.props.fallback;

            const isDev = process.env.NODE_ENV === 'development';

            return (
                <div className="min-h-[60vh] flex items-center justify-center p-6 bg-white">
                    <div className="max-w-md w-full bg-white border-2 border-rose-200 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                                <AlertCircle className="w-5 h-5 text-rose-600" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-[15px] font-bold text-slate-900">Une erreur est survenue</h2>
                                <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
                                    L&apos;interface a rencontré un problème inattendu. Rechargez la page pour réessayer.
                                </p>
                            </div>
                        </div>

                        {isDev && this.state.error && (
                            <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 overflow-auto max-h-40 font-mono text-slate-700">
                                {this.state.error.message}
                                {this.state.error.stack && '\n\n' + this.state.error.stack.split('\n').slice(0, 5).join('\n')}
                            </pre>
                        )}

                        <div className="flex gap-2">
                            <button
                                onClick={this.handleReset}
                                className="flex-1 px-3 py-2 text-[12px] font-semibold bg-white border border-slate-200 rounded-lg hover:border-[#000091]/40 hover:bg-[#000091]/[0.03] transition-colors"
                            >
                                Réessayer
                            </button>
                            <button
                                onClick={this.handleReload}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-semibold bg-[#000091] hover:bg-[#1e40af] text-white rounded-lg transition-colors"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Recharger la page
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
